import React, { useState, useEffect } from "react";
import { X, Plus } from "lucide-react";
import "./MyQueriesPopup.css";
import { queryService } from "../../../services/QueryService";
import { help_desk_query } from "../QueryChat/QueryChatPopup";
import { toast } from "../../../utils/toast";
import { LoginContext } from "../../../context/LoginContext";
import { useContext } from "react";
import { useNavigate } from "react-router-dom";

interface MyQueriesPopupProps {
  isOpen?: boolean;
}

interface Query {
  id: string;
  queryId: string;
  user_email: string;
  subject: string;
  category: string;
  status: "Open" | "Resolved";
  last_updated_at: string;
  user_name: string;
  drive_id: string;
  user_id: string;
}

export default function MyQueriesPopup({
  // isOpen,
  // onClose,
  // openQueryChat,
}: MyQueriesPopupProps) {
  const navigate = useNavigate();
  const isOpen = true
  const onClose = () => navigate("/applicant/home")
  const openQueryChat = (query?: help_desk_query) => {
    navigate(`/query/${query?.id}`)
  }
  const { user } = useContext(LoginContext);
  const [queries, setQueries] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);

  /* Pagination State */
  const [currentPage, setCurrentPage] = useState(1);
  const queriesPerPage = 6;

  useEffect(() => {
    if (isOpen) {
      fetchMyQueries();
    }
  }, [isOpen]);


  const fetchMyQueries = async () => {
    setLoading(true);
    try {
      const userId = user?.userId;
      if (!userId) return;

      const data = await queryService.getByFilter(
        "GET_QUERY_BY_USER_ID",
        `user_id%3A${userId}`
      );

      setQueries(data);
      setCurrentPage(1); // reset page
    } catch (error) {
      console.error("Error fetching queries:", error);
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  const formatDate = (dateStr: string) => {
    if (!dateStr) return "";
    try {
      const cleaned = dateStr.replace(/\[.*\]$/, "");
      const date = new Date(cleaned);
      if (isNaN(date.getTime())) return dateStr;
      return date.toLocaleString("en-IN", {
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        hour12: true,
      });
    } catch {
      return dateStr;
    }
  };

  /* Pagination Logic */

  const indexOfLast = currentPage * queriesPerPage;
  const indexOfFirst = indexOfLast - queriesPerPage;
  const currentQueries = queries.slice(indexOfFirst, indexOfLast);
  const totalPages = Math.ceil(queries.length / queriesPerPage);
  const handleStatusChange = async (query: Query, newStatus: string) => {
    try {
      const updateObj: Record<string, string> = {
        id: query.id,
        subject: query.subject,
        category: query.category,
        status: newStatus,
        user_id: query.user_id,
        drive_id: query.drive_id || "",
      };
      const jsonString = JSON.stringify(updateObj);
      const base64Encoded = btoa(jsonString);

      await queryService.update(query.id, base64Encoded);
      fetchMyQueries();
    } catch (error) {
      console.error("Error updating query status:", error);
      toast.error("Failed to update status.");
    }
  };

  return (
    <div className="myquery-overlay" onClick={onClose}>
      <div className="myquery-container" onClick={(e) => e.stopPropagation()}>

        {/* Header */}
        <div className="myquery-header">
          <div className="myquery-title">My Queries</div>

          <div className="closeBox">
            <button className="raise-query-btn" onClick={() => navigate("/newQuery")}>
              <Plus size={16} /> Raise New Query
            </button>

            <button className="help-popup-close" onClick={onClose}>
              <X size={20} />
            </button>
          </div>
        </div>

        {/* Query List */}
        <div className="query-list">
          {loading ? (
            <div style={{ padding: "20px", textAlign: "center" }}>Loading...</div>
          ) : queries.length === 0 ? (
            <div style={{ padding: "20px", textAlign: "center" }}>
              No queries found.
            </div>
          ) : (
            currentQueries.map((q: any, i: number) => (
              <div
                className="query-row"
                key={q.id || i}
              >
                <div className="query-box"
                  onClick={() => openQueryChat(q)}
                >

                  <div className="query-left">
                    <h4>{q.subject || "Untitled Query"}</h4>
                  </div>
                  <div className="query-right">
                    <span
                      className={
                        q.status === "Open"
                          ? "status open" :
                          q.status === "Resolved" ?
                            "status resolved" :
                            q.status === "In Progress" ?
                              "status update" : "status closed"
                      }
                    >
                      {q.status}
                    </span>

                    <div className="submitted-date">
                      Submitted : {formatDate(q.last_updated_at)}
                    </div>
                  </div>
                </div>

                <div className="status-update">
                  <span
                    style={{ cursor: q.status === "Closed" ? "default" : "pointer" }}
                    className={q.status === "Closed" ? "update query closed" : "update query"}
                    onClick={() => q.status !== "Closed" ? handleStatusChange(q, "Closed") : null}
                  >
                    {q.status !== "Closed" ? "Mark Closed" : "Closed"}
                  </span>
                </div>
              </div>
            ))
          )}
        </div>

        {/* Pagination Footer */}
        {queries.length > queriesPerPage && (
          <div className="query-pagination">

            <button
              disabled={currentPage === 1}
              onClick={() => setCurrentPage(currentPage - 1)}
            >
              Previous
            </button>

            <span>
              Page {currentPage} of {totalPages}
            </span>

            <button
              disabled={currentPage === totalPages}
              onClick={() => setCurrentPage(currentPage + 1)}
            >
              Next
            </button>

          </div>
        )}

      </div>
    </div>
  );
}